import { useDispatch } from 'react-redux';
import { useTracking } from 'react-tracking';

import { useMount } from '@simplywallst/ui-core';

import { BriefModalOnLoad } from '@/components/BriefModal';
import { fetchPortfolioList } from '@/components/DeprecatedPortfolio/redux/routines';
import DocumentHeader from '@/components/DocumentHeader';
import { useStream } from '@/components/Stream/hooks/useStream';
import { CompanyNoteModalProvider } from '@components/Note';

import type { Entity } from '../../redux/selectors/myMarketsAndIndustriesSelectors';

import { useIsMobileScreen } from '@/hooks/useMedia';
import useUser from '@/hooks/useUser';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { EventsModal } from '../EventsModal';

import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart';

import { DashboardDesktop } from './components/DashboardDesktop';
import { DashboardMobile } from './components/DashboardMobile';

export interface Props {
  myMarketKeys: string[];
  myIndustryKeys: string[];
  myMarketAndIndustryEntities: Record<string, Entity>;
  myMarketsAndIndustriesLastUpdated: number | null;
  subRoute?: string;
}

const Dashboard = ({
  subRoute,
  myMarketKeys,
  myIndustryKeys,
  myMarketAndIndustryEntities,
  myMarketsAndIndustriesLastUpdated,
}: Props) => {
  const dispatch = useDispatch();

  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { data: user } = useUser();

  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'page',
  });

  useStream();

  useMount(() => {
    dispatch(fetchPortfolioList.trigger());
  });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  if (!user) return null;

  if (isMobileScreen) {
    return (
      <Track>
        <MiniPriceChartDefinitions />
        <CompanyNoteModalProvider>
          <DocumentHeader title="Dashboard" />
          <BriefModalOnLoad />
          <EventsModal />
          <DashboardMobile
            subRoute={subRoute}
            isUserLoggedIn={user.loggedIn}
            isOnboardingSlideShowDismissed={
              user.portfolio?.hasDismissedDashboardOnboarding === true
            }
            myMarketKeys={myMarketKeys}
            myIndustryKeys={myIndustryKeys}
            myMarketAndIndustryEntities={myMarketAndIndustryEntities}
            myMarketsAndIndustriesLastUpdated={
              myMarketsAndIndustriesLastUpdated
            }
          />
        </CompanyNoteModalProvider>
      </Track>
    );
  }

  return (
    <Track>
      <MiniPriceChartDefinitions />
      <CompanyNoteModalProvider>
        <DocumentHeader title="Dashboard" />
        <BriefModalOnLoad />
        <EventsModal />
        <DashboardDesktop
          isUserLoggedIn={user.loggedIn}
          isOnboardingSlideShowDismissed={
            user.portfolio?.hasDismissedDashboardOnboarding === true
          }
          myMarketKeys={myMarketKeys}
          myIndustryKeys={myIndustryKeys}
          myMarketAndIndustryEntities={myMarketAndIndustryEntities}
          myMarketsAndIndustriesLastUpdated={myMarketsAndIndustriesLastUpdated}
        />
      </CompanyNoteModalProvider>
    </Track>
  );
};

export default Dashboard;
